import { normalizeStructuredFilterGroup } from "./normalizeStructuredFilterGroup";

import {
  type AnalyticsGraphArtifact,
  AnalyticsGraphFormat,
} from "@/queries/analytics-custom-charts-types";

const normalizeAgChartsGraphArtifact = (
  artifact: AnalyticsGraphArtifact,
): AnalyticsGraphArtifact => {
  if (artifact.format !== AnalyticsGraphFormat.AgCharts) {
    return artifact;
  }

  const filterGroup = artifact.filterGroup ?? artifact.filter_group;
  if (!filterGroup) {
    return artifact;
  }

  const normalizedFilterGroup = normalizeStructuredFilterGroup(filterGroup);
  return {
    ...artifact,
    filterGroup: normalizedFilterGroup,
    filter_group: undefined,
  };
};

const isValidGraphArtifact = (artifact: AnalyticsGraphArtifact): boolean => {
  if (artifact.format === AnalyticsGraphFormat.AgCharts) {
    return Array.isArray(artifact.charts) && artifact.charts.length > 0;
  }
  return typeof artifact.diagram === "string" && artifact.diagram.trim().length > 0;
};

const parseAnalyticsGraphFromToolResult = (
  raw: Record<string, unknown> | undefined,
): AnalyticsGraphArtifact | null => {
  if (!raw) {
    return null;
  }

  if (raw.success === false) {
    return null;
  }

  const { graph } = raw;
  if (!graph || typeof graph !== "object") {
    return null;
  }

  const artifact = normalizeAgChartsGraphArtifact(graph as AnalyticsGraphArtifact);
  return isValidGraphArtifact(artifact) ? artifact : null;
};

export const parseAnalyticsGraphFromToolCallOutput = (
  output: unknown,
): AnalyticsGraphArtifact | null => {
  if (typeof output === "string") {
    try {
      const parsed = JSON.parse(output) as Record<string, unknown>;
      return parseAnalyticsGraphFromToolResult(parsed);
    } catch {
      return null;
    }
  }

  if (output && typeof output === "object" && !Array.isArray(output)) {
    return parseAnalyticsGraphFromToolResult(output as Record<string, unknown>);
  }

  return null;
};
